/**
 * Decorative cassette-style color bars.
 *
 * Used both as crisp inter-section dividers (`orientation="horizontal"`,
 * inspired by the T-120 STEREO VIDEO CASSETTE reference) and as energetic
 * angled backdrops behind hero/projects/footer
 * (`orientation="diagonal"`, inspired by Ocean Drive / Rainbow / Videowaves /
 * T-90 references).
 *
 * The component is purely visual and is marked `aria-hidden`.
 */
type Orientation = 'horizontal' | 'diagonal';

type Props = {
  orientation?: Orientation;
  /** Tailwind-resolvable CSS colors for the stripes, in order. */
  colors?: string[];
  /** Stripe thickness in px (only meaningful for diagonal). */
  stripe?: number;
  /** Angle in degrees for diagonals (default 45). */
  angle?: number;
  /** Height in tailwind h-* class (e.g. "h-3", "h-6", "h-full"). */
  height?: string;
  className?: string;
};

const defaultPalette = ['#3A6E8A', '#6B7A3F', '#B49533', '#C5683B', '#8B2D3A'];

const ColorBars = ({
  orientation = 'horizontal',
  colors = defaultPalette,
  stripe = 28,
  angle = 45,
  height = 'h-4',
  className = '',
}: Props) => {
  if (orientation === 'horizontal') {
    const step = 100 / colors.length;
    const stops = colors
      .map((c, i) => `${c} ${i * step}% ${(i + 1) * step}%`)
      .join(', ');
    return (
      <div
        aria-hidden
        className={`${height} w-full ${className}`}
        style={{ backgroundImage: `linear-gradient(to right, ${stops})` }}
      />
    );
  }

  // Diagonal: build a repeating-linear-gradient that pairs each color band
  // with a transparent gap so the stripes feel like distinct ribbons.
  const band = stripe;
  const cycle = band * colors.length;
  const stops = colors
    .map((c, i) => `${c} ${i * band}px ${(i + 1) * band}px`)
    .join(', ');
  return (
    <div
      aria-hidden
      className={`absolute inset-0 ${className}`}
      style={{
        backgroundImage: `repeating-linear-gradient(${angle}deg, ${stops}, transparent ${cycle}px, transparent ${cycle + band * 2}px)`,
      }}
    />
  );
};

export default ColorBars;
